import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import compose from "recompose/compose";
import isEqual from "lodash/isEqual";

import { crudGetOne, crudGetList, crudGetMany, translate, RestMethods, FETCH_START, FETCH_END } from "jazasoft";

import { dataProvider } from "../../../App";
import { getDistinctValues } from "../../../utils/helpers";
import { cbSortFlow } from "../../../helpers/orderHelper";
import handleError from "../../../utils/handleError";
import hasPrivilege from "../../../utils/hasPrivilege";
import Forbidden from "../../../components/Forbidden";
import { OState } from "../../../utils/types";
import { ResourceId, Scope } from "../../../utils/privilege";

export class MicroController extends Component {
  state = {
    orderFlowMap: {} // key: flowTabIdx, value: OrderConfig
  };

  componentDidMount() {
    this.updateOrder();
    this.init();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.id !== nextProps.id || nextProps.version !== this.props.version) {
      this.updateOrder(nextProps.resource, nextProps.id);
    }
    if (!isEqual(this.props.record, nextProps.record)) {
      this.init(nextProps.record);
    }
  }

  init = (order = this.props.record) => {
    if (order) {
      const orderFlowList = order.orderFlowList.filter(of => of.stateValue >= OState.SHRINKAGE_GROUPING_COMPLETED.stateValue);

      let orderFlowMap = {};
      let flowTabIdx = 0;
      orderFlowList
        .sort((a, b) => cbSortFlow(a, b))
        .forEach(oc => {
          orderFlowMap[flowTabIdx++] = oc;
        });

      this.setState({ orderFlowMap });
    }
  };

  updateOrder = (resource = this.props.resource, id = this.props.id) => {
    if (!id) return;
    this.props.dispatch(crudGetOne(resource, id, this.props.basePath, this.props.requestConfig));
  };

  updateFabrics = fabricIds => {
    fabricIds = getDistinctValues(fabricIds);
    if (fabricIds && fabricIds.length > 0) {
      this.props.dispatch(crudGetMany("fabrics", fabricIds));
    }
  };

  updateFabricsDeep = fabricIds => {
    fabricIds = getDistinctValues(fabricIds);
    if (fabricIds && fabricIds.length > 0) {
      fabricIds.forEach(fabricId => {
        this.props.dispatch(crudGetOne("fabrics", fabricId));
      });
    }
  };

  fetchSettings = cb => {
    const options = {
      url: `settings`,
      method: "get",
      params: { size: 100 }
    };
    this.props.dispatch({ type: FETCH_START });
    dataProvider(RestMethods.CUSTOM, undefined, options)
      .then(response => {
        cb && cb(response.data?.content);
        this.props.dispatch({ type: FETCH_END });
      })
      .catch(err => {
        console.log(err);
        this.props.dispatch({ type: FETCH_END });
      });
  };

  fetchParts = () => {
    this.props.dispatch(crudGetList("bundleParts", { page: 0, pageSize: 50 }));
  };

  fetchLaySlip = (orderFlow, cb) => {
    if (orderFlow) {
      const options = {
        url: `orders/${this.props.id}/lays`,
        method: "get",
        params: { flowId: orderFlow.id }
      };
      this.props.dispatch({ type: FETCH_START });
      dataProvider(RestMethods.CUSTOM, undefined, options)
        .then(response => {
          cb && cb(response.data);
          this.props.dispatch({ type: FETCH_END });
        })
        .catch(err => {
          cb && cb(null, err);
          handleError(err, this.props.dispatch);
          this.props.dispatch({ type: FETCH_END });
        });
    }
  };

  fetchBundles = (orderFlow, cb) => {
    const options = {
      url: `orders/${this.props.id}/bundles`,
      method: "get",
      params: {
        flowId: orderFlow.id,
        layNo: -1
      }
    };
    this.props.dispatch({ type: FETCH_START });

    dataProvider(RestMethods.CUSTOM, undefined, options)
      .then(response => {
        cb && cb(response.data);
        this.props.dispatch({ type: FETCH_END });
      })
      .catch(err => {
        cb && cb(null, err);
        handleError(err, this.props.dispatch);
        this.props.dispatch({ type: FETCH_END });
      });
  };

  fetchReport = (style, color, product, part, lot, cb) => {
    let items = [];
    items.push(`orderId==${this.props.id}`);
    items.push(`style=="${style}"`);
    items.push(`color=="${color}"`);
    items.push(`product=="${product}"`);
    items.push(`part=="${part}"`);
    items.push(`lot==${lot}`);

    const options = {
      url: "reports",
      method: "get",
      params: { search: items.join(";") }
    };
    this.props.dispatch({ type: FETCH_START });
    dataProvider(RestMethods.CUSTOM, undefined, options)
      .then(response => {
        if (response.status === 200) {
          cb && cb(response.data.content);
        }
        this.props.dispatch({ type: FETCH_END });
      })
      .catch(err => {
        cb && cb(null, err);
        handleError(err, this.props.dispatch);
        this.props.dispatch({ type: FETCH_END });
      });
  };

  render() {
    const {
      id,
      basePath,
      resource,
      isLoading,
      saving,
      format,
      record,
      fabrics,
      parts,
      roles,
      hasAccess,
      history,
      children,
      dispatch,
      version,
      translate
    } = this.props;

    if (!children) return null;

    if (!hasPrivilege(roles, hasAccess, ResourceId.MICRO, Scope.READ)) {
      return <Forbidden history={history} />;
    }

    return children({
      id,
      isLoading,
      saving,
      resource,
      i18nKey: "report",
      basePath,
      order: format(record),
      fabrics,
      parts,
      fetchReport: this.fetchReport,
      updateFabrics: this.updateFabrics,
      updateFabricsDeep: this.updateFabricsDeep,
      fetchLaySlip: this.fetchLaySlip,
      fetchParts: this.fetchParts,
      fetchBundles: this.fetchBundles,
      fetchSettings: this.fetchSettings,
      translate,
      version,
      dispatch,
      ...this.state
    });
  }
}

MicroController.propTypes = {
  //props provided by user
  className: PropTypes.string,
  classes: PropTypes.object,
  children: PropTypes.func.isRequired,
  requestConfig: PropTypes.object,
  format: PropTypes.func,
  //props from state
  record: PropTypes.object,
  isLoading: PropTypes.bool,
  translate: PropTypes.func.isRequired,
  // from framework
  basePath: PropTypes.string,
  resource: PropTypes.string,
  referenceResource: PropTypes.string,
  i18nKey: PropTypes.string
};

MicroController.defaultProps = {
  requestConfig: {},
  format: record => record
};

const mapStateToProps = (state, props) => {
  const resourceState = state.jazasoft.resources[props.resource];
  const id = props.match.params.id;
  return {
    id,
    record: resourceState && resourceState.data[id],
    fabrics: state.jazasoft.resources["fabrics"] && state.jazasoft.resources["fabrics"].data,
    parts: state.jazasoft.resources["bundleParts"] && state.jazasoft.resources["bundleParts"].data,
    isLoading: state.jazasoft.loading > 0,
    saving: state.jazasoft.saving,
    version: state.jazasoft.ui.viewVersion
  };
};

export default compose(translate, connect(mapStateToProps))(MicroController);
